import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { MarketingLayout } from "@/components/MarketingLayout";
import { Seo } from "@/components/Seo";
import { SignalCard } from "@/components/SignalCard";
import { DemoBadge } from "@/components/DemoBadge";
import { StatCard, CardSkeleton } from "@/components/Bits";
import { Disclaimer } from "@/components/Disclaimer";

export default function Signals() {
  const [signals, setSignals] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [market, setMarket] = useState("");
  const [direction, setDirection] = useState("");

  useEffect(() => {
    api.get("/signals?limit=60").then((r) => setSignals(r.data.signals)).catch(() => setSignals([]));
    api.get("/signals/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  }, []);

  const filtered = (signals || []).filter((s) =>
    (!market || s.market === market) && (!direction || s.direction.includes(direction))
  );

  return (
    <MarketingLayout>
      <Seo title="AI Signal Transparency" description="Public AI signal history including losing signals. Full transparency." />
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-accent">Transparency</span>
            <h1 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">AI Signal Center & Transparency</h1>
            <p className="mt-2 max-w-2xl text-muted-foreground">We never hide losing signals. Every published setup — win, loss or expired — is shown below with full analytics.</p>
          </div>
          <DemoBadge />
        </div>

        {analytics && (
          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-6">
            <StatCard label="Total Signals" value={analytics.total_signals} testid="stat-total" />
            <StatCard label="Wins" value={analytics.wins} accent="text-bullish" testid="stat-wins" />
            <StatCard label="Losses" value={analytics.losses} accent="text-bearish" testid="stat-losses" />
            <StatCard label="Win Rate" value={`${analytics.win_rate}%`} testid="stat-winrate" />
            <StatCard label="Avg R/R" value={analytics.avg_rr} testid="stat-rr" />
            <StatCard label="Max Drawdown" value={`${analytics.max_drawdown}R`} accent="text-warning" testid="stat-dd" />
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-3">
          <select value={market} onChange={(e) => setMarket(e.target.value)} data-testid="filter-market" className="rounded-lg border border-border bg-card px-3 py-2 text-sm">
            <option value="">All markets</option>
            {["crypto", "forex", "stocks", "indices", "commodities"].map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select value={direction} onChange={(e) => setDirection(e.target.value)} data-testid="filter-direction" className="rounded-lg border border-border bg-card px-3 py-2 text-sm">
            <option value="">All directions</option>
            <option value="BUY">Buy / Long</option>
            <option value="SELL">Sell / Short</option>
            <option value="WAIT">Wait / No Trade</option>
          </select>
        </div>

        {!signals ? (
          <div className="mt-8"><CardSkeleton count={6} /></div>
        ) : (
          <div className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((s, i) => <SignalCard key={s.asset + s.timeframe + i} s={s} />)}
          </div>
        )}

        <div className="mt-10"><Disclaimer /></div>
      </div>
    </MarketingLayout>
  );
}
